import queryString from 'query-string'

import { useLocation, useSearchParams } from 'react-router-dom'
import I18n from 'i18n-js'
import { FC, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { navigate } from 'builder/modules/navigate'
import TemplatesLayout from '../TemplatesGuest/TemplatesLayout'

import {
  Subtitle,
  Title,
  Wrapper,
  Container,
  ButtonsContainer,
  SkipButton,
  ContinueButton,
  CategoryChoiceContainer,
  InputContainer,
  CategoryGridContainer,
  CategoryTitle,
  ItemIcon,
} from './styles'

import {
  AiInterviewPrepIcon,
  AutoApplyIcon,
  CareerPathwayIcon,
  InstantCoverLetterIcon,
  OneOneCoachingIcon,
  PersonalJobAlertsIcon,
  RecruiterMatchIcon,
  ResumeTailoringIcon,
  SalaryAnalyzerIcon,
} from './icons'

type Category = {
  label: string
  value: string
  position: number
  icon: ({ selected }: { selected: boolean }) => JSX.Element
}

const existingCategories = [
  {
    label: 'Resume tailoring',
    value: 'resume_tailoring',
    position: 1,
    icon: ResumeTailoringIcon,
  },
  {
    label: 'Instant cover letter',
    value: 'instant_cover_letter',
    position: 2,
    icon: InstantCoverLetterIcon,
  },
  {
    label: 'Recruiter match',
    value: 'recruiter_match',
    position: 3,
    icon: RecruiterMatchIcon,
  },
  {
    label: 'Auto Apply',
    value: 'auto_apply',
    position: 4,
    icon: AutoApplyIcon,
  },
  {
    label: 'Personal job alerts',
    value: 'personal_job_alerts',
    position: 5,
    icon: PersonalJobAlertsIcon,
  },
  {
    label: 'Salary analyzer',
    value: 'salary_analyzer',
    position: 6,
    icon: SalaryAnalyzerIcon,
  },
  {
    label: 'AI interview prep',
    value: 'ai_interview_prep.svg',
    position: 7,
    icon: AiInterviewPrepIcon,
  },
  {
    label: '1-1 coaching',
    value: '1_1_coaching',
    position: 8,
    icon: OneOneCoachingIcon,
  },
  {
    label: 'Career pathways',
    value: 'career_pathways',
    position: 9,
    icon: CareerPathwayIcon,
  },
] as Category[]

type CategoryChoiceProps = {
  selected: boolean
  setSelected: (category: Category, selected: boolean) => void
  category: Category
  icon: ({ selected }: { selected: boolean }) => JSX.Element
}

export const CategoryChoice: FC<CategoryChoiceProps> = ({
  selected,
  setSelected,
  icon,
  category,
}) => {
  const [isSelected, setIsSelected] = useState<boolean>(selected)

  const toggleSelect = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    const newValue = !isSelected

    setIsSelected(newValue)
    setSelected(category, newValue)
  }

  return (
    <CategoryChoiceContainer selected={isSelected} onClick={toggleSelect}>
      <InputContainer checked={isSelected} />
      <ItemIcon>{icon({ selected: isSelected })}</ItemIcon>
      <CategoryTitle>{category.label}</CategoryTitle>
    </CategoryChoiceContainer>
  )
}

const UserCategoryChoice = () => {
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([])
  const [searchParams] = useSearchParams()
  const documentId = searchParams.getAll('documentId')

  const toggleSelectedCategory = (category: Category, value: boolean) => {
    if (!value) {
      setSelectedCategories(selectedCategories.filter(item => item.value !== category.value))
    } else {
      setSelectedCategories([...selectedCategories, category])
    }
  }

  const continueToNextStep = () => {
    if (!documentId || documentId.length === 0) {
      navigate('/')
      return
    }
    navigate(`/resumes/${documentId}/edit`, { replace: true })
  }

  const handleSkipButton = () => {
    trackInternalEvent('skip_feature_selection', { label: 'funnel' })
    continueToNextStep()
  }

  const handleContinueButton = () => {
    const selectedValues = selectedCategories.map(category => category.value)

    trackInternalEvent('click_continue_feature_selection', {
      label: 'funnel',
      number_of_features: selectedValues.length,
      selected_features: selectedValues,
    })

    continueToNextStep()
  }

  return (
    <>
      <Wrapper>
        <Container>
          <Title>{I18n.t('builder.sign_up.users_feature_preferences')}</Title>
          <Subtitle>{I18n.t('builder.sign_up.users_feature_preferences_subtitle')}</Subtitle>
          <CategoryGridContainer>
            {existingCategories.map((category, index) => {
              return (
                <CategoryChoice
                  key={index}
                  selected={
                    selectedCategories.find(item => item.value === category.value) !== undefined
                  }
                  setSelected={toggleSelectedCategory}
                  icon={category.icon}
                  category={category}
                />
              )
            })}
          </CategoryGridContainer>
          <ButtonsContainer>
            <SkipButton onClick={handleSkipButton}>Skip</SkipButton>
            <ContinueButton onClick={handleContinueButton}>Continue</ContinueButton>
          </ButtonsContainer>
        </Container>
      </Wrapper>
    </>
  )
}

export const UserSelectionView = () => {
  const location = useLocation()
  // Preserve initial query parameters
  const urlParams = queryString.parse(location.search)
  const regionPrefix = (urlParams.current_region_prefix as string) || ''

  return (
    <>
      <TemplatesLayout
        regionPrefix={regionPrefix}
        handleSkipButton={() => {}}
        userCategoryPageSelection={true}
      >
        <UserCategoryChoice />
      </TemplatesLayout>
    </>
  )
}
